<template>
  <div v-if="!submitted">
    <form novalidate @submit.prevent="handleSubmit">
      <h3 class="!mb-6 md:!mb-8">{{ title }}</h3>

      <div class="flex flex-col gap-4 md:gap-6">
        <InputBase
          v-model="formData.first_name"
          label="First Name"
          :error="formErrors.first_name"
          type="text"
          :icon="IconPerson"
        />

        <InputBase
          v-model="formData.last_name"
          label="Last Name"
          :error="formErrors.last_name"
          type="text"
          :icon="IconPerson"
        />
        <InputBase
          v-model="formData.phone_number"
          label="Phone"
          :error="formErrors.phone_number"
          type="tel"
        />

        <InputBase
          v-model="formData.email_address"
          label="Email"
          :error="formErrors.email_address"
          type="email"
        />

        <InputBase
          v-model="formData.business_name"
          label="Company Name"
          :error="formErrors.business_name"
          type="text"
          :icon="IconBuilding"
        />

        <InputSelectBase
          v-model="formData.number_of_employees"
          label="Company Size (number of employees)"
          :error="formErrors.number_of_employees"
          :options="[
            { value: '10', text: 'Less than 100' },
            { value: '100', text: 'Between 100 and 500' },
            { value: '500', text: 'More than 500' },
          ]"
        />
      </div>

      <Alert
        v-if="error"
        type="fatal"
        size="small"
        class="mt-6 md:mt-8"
        :content="{ body: error, isVhtml: true }"
      />
      <BtnPrimary
        label="Download ebook"
        size="large"
        arrow="right"
        class="my-6 w-full md:my-8"
        type="submit"
        :loading="loading"
      />
      <p class="text-sm font-light">
        Your privacy is important to us. Please review our
        <NuxtLink
          to="https://assets.aussiebroadband.com.au/web/assets/legal/policies/abb-privacy-policy.pdf"
          class="text-inherit"
          :external="true"
          target="_blank"
          >privacy policy
        </NuxtLink>
        to see how we protect your information.
      </p>
    </form>
  </div>
  <div v-if="submitted" class="mb-6">
    <div class="tag">Download Ready</div>
    <h3>Here's the brochure you requested .</h3>
    <p>
      In the next few days, one of our telecommunication experts may contact you
      to answer any questions you have and if necessary, provide a tailored
      solution & quote.
    </p>
    <BtnPrimary
      label="Download"
      :url="link"
      size="large"
      arrow="none"
      target="_blank"
      class="mb-6 w-full"
      @click="
        {
          $emit('closeModal');
          submitted = false;
        }
      "
    />
    <p>
      If you have any questions before then, please give us a quick call on
      <NuxtLink :to="enterprisePhone.sales.url">{{
        enterprisePhone.sales.text
      }}</NuxtLink
      >.
    </p>
  </div>
</template>

<script setup lang="ts">
import type { LeadMethod, GenericLead } from "~/types/genericLead";
import IconPerson from "@/components/icon/Person.vue";
import IconBuilding from "@/components/icon/Building.vue";

const props = defineProps<{
  title: string;
  link: string;
  modalActive: boolean;
  leadSource?: LeadMethod; // e.g. "Managed Services Form"
  propertyType?: GenericLead["property_type"];
}>();
const emit = defineEmits(["closeModal"]);
const { enterprisePhone } = getPhoneNumbers();
const title = computed(() => props.title || "Get your free ebook");
const formData = reactive({
  property_type: props.propertyType || "enterprise",
  lead_source: props.leadSource || "EBOOK_DOWNLOAD",
  first_name: "",
  last_name: "",
  phone_number: "",
  email_address: "",
  business_name: "",
  number_of_employees: "",
  notes: "",
});

const formErrors = reactive({
  first_name: "",
  last_name: "",
  phone_number: "",
  email_address: "",
  business_name: "",
  number_of_employees: "",
});

const clearErrors = () => {
  formErrors.first_name = "";
  formErrors.last_name = "";
  formErrors.phone_number = "";
  formErrors.email_address = "";
  formErrors.business_name = "";
  formErrors.number_of_employees = "";
};

const loading = ref(false);
const submitted = ref(false);
const modalActive = computed(() => props.modalActive);
const error = ref("");

watch(modalActive, () => {
  if (!modalActive.value) submitted.value = false;
});

const handleSubmit = async () => {
  loading.value = true;
  error.value = "";
  clearErrors();

  formErrors.first_name = validateRequired(
    formData.first_name,
    "First name is required",
  );
  formErrors.last_name = validateRequired(
    formData.last_name,
    "Last name is required",
  );
  formErrors.phone_number = validatePhone(formData.phone_number);
  formErrors.email_address = validateEmail(formData.email_address);
  formErrors.business_name = validateRequired(
    formData.business_name,
    "Company name is required",
  );
  formErrors.number_of_employees = validateRequired(
    formData.number_of_employees,
    "Company size is required",
  );

  if (Object.values(formErrors).some((err) => err.length > 0)) {
    loading.value = false;
    return;
  }

  if (formData.notes.length === 0) formData.notes = "no";

  createGenericLead(formData as GenericLead)
    .then(() => {
      submitted.value = true;
    })
    .catch((e) => {
      error.value = e.message;
      //error.value = "Something went wrong. Please try again later.";
    })
    .finally(() => {
      loading.value = false;
    });
};
</script>
